import { GraphConfiguration } from "@/type/common";
import axios from "@/services/axios";

// Helper to pick only relevant fields for hashing
export function getRelevantConfig(config: GraphConfiguration) {
  return {
    model: config.model,
    type: config.type,
    data_source: config.data_source,
    graph_options: config.graph_options,
    measures: config.graph_options.measures,
    columns: config.table_options.columns,
    page_size: config.table_options.pageSize,
    pagination: config.table_options.pagination,
    blockField: config.block_options.field,
    blockAggregation: config.block_options.aggregation,
    only_count_graph: config.graph_options.only_count,
  };
}

// Simple hash function for strings
function simpleHash(str: string): string {
  let hash = 0,
    i,
    chr;
  if (str.length === 0) return hash.toString();
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash.toString();
}

// Cache duration in ms (10 minutes)
const CACHE_DURATION = 10 * 60 * 1000;

// Use a single cache key for all data
const ALL_CACHE_KEY = "data-cache";

const getCache = () => {
  const cacheRaw = localStorage.getItem(ALL_CACHE_KEY);
  let cache = {};
  if (cacheRaw) {
    try {
      cache = JSON.parse(cacheRaw);
    } catch (e) {}
  }
  return cache;
};

export const loadData = async (
  config: GraphConfiguration,
  blockCache: boolean = false,
  fields: any[] = []
) => {
  const relevantConfig = getRelevantConfig(config);

  // Get user context for cache invalidation when language/timezone changes
  let userContext = {};
  try {
    // Fetch user context (language, timezone) from backend
    const userContextResponse = await axios.post("/odash/execute", {
      action: "get_user_context",
      parameters: {},
    });
    if (userContextResponse.data) {
      userContext = {
        lang: userContextResponse.data.lang || "en_US",
        tz: userContextResponse.data.tz || "UTC",
        date_format: userContextResponse.data.date_format || "%m/%d/%Y",
        time_format: userContextResponse.data.time_format || "%H:%M",
      };
    }
  } catch (error) {
    // Fallback to timestamp-based invalidation if user context fetch fails
    console.warn(
      "Failed to fetch user context for cache key, using timestamp fallback:",
      error
    );
    userContext = {
      timestamp: Math.floor(Date.now() / (1000 * 60 * 5)), // Refresh every 5 minutes as fallback
    };
  }

  const configWithUserContext = { ...relevantConfig, userContext };
  const hashConfig = simpleHash(JSON.stringify(configWithUserContext));
  const now = new Date();

  if (config.data_source.groupBy) {
    config.data_source.groupBy = config.data_source.groupBy.map((g) => {
      const groupByField = (
        fields && fields.length > 0 ? fields : config.fields
      )?.find((f) => f.field === g.field);
      if (!groupByField || !["date", "datetime"].includes(groupByField.type)) {
        return {
          field: g.field,
          show_empty: g.show_empty ? true : false,
        };
      }
      return {
        field: g.field,
        interval: g.interval,
        show_empty: g.show_empty ? true : false,
      };
    });
  }
  let allCache: any = getCache();

  if (!blockCache) {
    const cache = allCache[config.id];
    if (
      cache &&
      cache.hashConfig === hashConfig &&
      new Date(cache.expirationDate) > now
    ) {
      return cache.data;
    }
  }

  // Fetch new data
  try {
    const response = await axios.post("/odash/execute", {
      action: "process_dashboard_request",
      parameters: { request_data: [config] },
    });
    const unifiedApiData = response.data;

    // Transform unified API response back to expected format for backward compatibility
    // Unified API returns: { [config.id]: { data: [...], metadata: {...} } }
    // Expected format: [{ [config.id]: { data: [...], metadata: {...} } }]
    const transformedData = [
      {
        [config.id]: unifiedApiData[config.id] || { data: [], metadata: null },
      },
    ];
    const metadata = unifiedApiData[config.id]?.metadata || null;
    

    // Save to cache
    if (!blockCache) {
      const expirationDate = new Date(
        now.getTime() + CACHE_DURATION
      ).toISOString();
      allCache = getCache();
      allCache[config.id] = {
        expirationDate,
        hashConfig,
        data: transformedData,
        metadata,
      };
      localStorage.setItem(ALL_CACHE_KEY, JSON.stringify(allCache));
    }

    return transformedData;
  } catch (e) {
    return null;
  }
};
